import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDevice } from "src/hooks/useDevice";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Button, Checkbox, FormControlLabel, styled } from "@mui/material";

import { makeStyles, withStyles } from "@mui/styles";

import "./ActivitiesPage.scss";
import {Activity, Page, Filter, HomepageProps} from "src/components/Interfaces";
import AddIcon from "@mui/icons-material/Add";
import ReactMarkdown from "react-markdown";
import ActivityTile from "src/components/ActivityTile/ActivityTile";
import { useActivityCategories } from "src/hooks/useActivityCategories";
import { useActivitySettings } from "src/hooks/useActivitySettings";
import { useActivities } from "src/hooks/useActivities";
import { useHomepage } from "src/hooks/useHomepage";
import { usePages } from "src/hooks/usePages";
import { decodeKey, encodeKey } from "src/utils/urlUtils";
import { getPageContentByType } from "src/utils/renderUtils";
import { appSettings } from "src/config";
import { LoadingSlider } from "src/components/LoadingSlider/LoadingSlider";
import { LoadingSpinner } from "src/components/LoadingSpinner/LoadingSpinner";

import HomepageIntro from "src/components/HomepageIntro/HomepageIntro";

const ACTIVITY_INCREMENT: number = 3;

function ActivitiesPage() {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const [visibleActivities, setVisibleActivities] = useState<Activity[]>([]);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [visibleActivitiesCount, setVisibleActivitiesCount] =
    useState<number>(48);

  const {
    isLoading: isActivityCategoriesLoading,
    error: activityCategoriesError,
    data: activityCategoriesData,
  } = useActivityCategories();

  const {
    isLoading: isActivitiesLoading,
    error: activitiesError,
    data: activitiesData,
  } = useActivities(false);

  const {
    isLoading: isHomepageLoading,
    error: homepageError,
    data: homepageProps
  } = useHomepage();

  const {
    isLoading: isPagesLoading,
    error: pagesError,
    data: pagesData,
  } = usePages();

  useEffect(() => {
    if (activitiesData) {
      filterActivities();
    }
  }, [activitiesData]);

  useEffect(() => {
    initializeFilters();
  }, [activityCategoriesData]);

  const initializeFilters = () => {
    if (activityCategoriesData) {
      const _filters: Filter[] = [];

      //default initialization
      activityCategoriesData.forEach((item) => {
        _filters.push({
          id: item.id,
          label: item.attributes.name,
          isActive: true,
        });
      });

      //setup active filters
      searchParams.forEach((v, k) => {
        if (k === "filters") {
          let keys = v.split(",");
          let decodedKeys: string[] = [];

          keys.map((key) => {
            let decodedKey = decodeKey(key);
            decodedKeys.push(decodedKey);
          });

          _filters.forEach((filter) => {
            filter.isActive = decodedKeys.indexOf(filter.id.toString()) > -1;
          });
        }
        if (k === "count") {
          const count = Number(v);
          if (count)
            setVisibleActivitiesCount(count);
        }
      });

      setFilters(_filters);
    }
  }

  useEffect(() => {
      filterActivities();
  }, [filters]);

  useEffect(() => {
      filterActivities();
  }, [visibleActivitiesCount]);


  const isCategoryChecked = (filterId: number) => {
    const filter = filters?.find((item) => item.id === filterId);
    if (filter) return filter.isActive;

    return false;
  };

  const filterActivities = () => {
    const filteredActivities: Activity[] = [];
    let totalCount = 0;

    activitiesData?.forEach((item, index) => {
      const category = item.attributes.activity_category?.data?.id;
      if (category && isCategoryChecked(category)) {
        if (totalCount < visibleActivitiesCount) {
          totalCount++;
          filteredActivities.push(item);
        }
      }
    });

    setVisibleActivities(filteredActivities);
  };

  const getTotalActivitiesCount = () => {
     let totalCount = 0;

    activitiesData?.forEach((item) => {
      const category = item.attributes.activity_category?.data?.id;
      if (category && isCategoryChecked(category)) {
          totalCount++;
      }
    });

    return totalCount;
  }

  if (isActivityCategoriesLoading || isActivitiesLoading || isPagesLoading)
    return (
      <>
        <LoadingSlider />
        <LoadingSpinner />
      </>
    );

  if (activityCategoriesError || activitiesError || pagesError)
    return <span>"An error has occurred: "</span>;

  return (
    <div className="ActivitiesPage">
      <div className="page-content">
        {homepageProps && (
          <HomepageIntro homepage={homepageProps} />
        )}
        <div className="activities-list">
          {visibleActivities?.map((activityItem, index) => {
            return <ActivityTile key={`activity_item_${activityItem.id}`} activity={activityItem} isArchive={false} />;
          })}
        </div>
        <div className="links-part">
          <a className="link-tile" href={`/archive`}>
            <div className="link-tile-image archive" />
            <div className="link-tile-label">{t("archive")}</div>
          </a>
        </div>
      </div>
    </div>
  );
}

export default ActivitiesPage;
